import { RefreshCw, Save, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import EmptyState from "../../components/EmptyState.jsx";
import { Field } from "../../components/FormControls.jsx";
import Modal from "../../components/Modal.jsx";
import RoutineBoard, { RoutineBoardLegend } from "../../components/RoutineBoard.jsx";
import SearchBar from "../../components/SearchBar.jsx";
import { SkeletonList } from "../../components/Skeleton.jsx";
import StatCard from "../../components/StatCard.jsx";
import { useAuth } from "../../context/AuthContext.jsx";
import { useToast } from "../../context/ToastContext.jsx";
import { adminUpdateTimeLog, getSettings, getTodayLogs, getTodayRoutines } from "../../firebase/firestore.js";
import { DAYS, getDayName, getNowTimeValue, getTodayKey } from "../../utils/date.js";
import { matchesSearch, searchableRoutine } from "../../utils/search.js";
import { timeRangeLabel } from "../../utils/time.js";

function dayFromDate(dateKey) {
  return getDayName(new Date(`${dateKey}T00:00:00`));
}

function makeLogFromRoutine(routine, date, day) {
  return {
    id: `${date}_${routine.id || routine.routineId}`,
    logId: `${date}_${routine.id || routine.routineId}`,
    date,
    day,
    logDate: new Date(`${date}T00:00:00`),
    routineId: routine.id || routine.routineId,
    teacherId: routine.teacherId || "",
    subjectId: routine.subjectId || "",
    classId: routine.classId || "",
    sectionId: routine.sectionId || "",
    roomId: routine.roomId || "",
    teacherName: routine.teacherName || "",
    subjectName: routine.subjectName || "",
    className: routine.className || "",
    sectionName: routine.sectionName || "",
    roomNumber: routine.roomNumber || "N/A",
    scheduledIn: routine.scheduledIn || "",
    scheduledOut: routine.scheduledOut || "",
    classStatus: "normal",
    suspensionReason: ""
  };
}

export default function RoutineBoardPage() {
  const [date, setDate] = useState(getTodayKey());
  const [day, setDay] = useState(getDayName());
  const [search, setSearch] = useState("");
  const [routines, setRoutines] = useState([]);
  const [logs, setLogs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const { user } = useAuth();
  const { showToast } = useToast();

  async function load(nextDate = date, nextDay = day) {
    setLoading(true);
    try {
      const [nextSettings, nextRoutines, nextLogs] = await Promise.all([
        settings ? Promise.resolve(settings) : getSettings(),
        getTodayRoutines(nextDay),
        getTodayLogs(nextDate)
      ]);
      setSettings(nextSettings);
      setRoutines(nextRoutines);
      setLogs(nextLogs);
    } catch (error) {
      showToast(error.message || "Unable to load routine board", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const logByRoutine = useMemo(() => Object.fromEntries(logs.map((log) => [log.routineId, log])), [logs]);
  const visibleRoutines = useMemo(() => {
    return routines.filter((routine) => {
      const log = logByRoutine[routine.id] || logByRoutine[routine.routineId];
      return matchesSearch(searchableRoutine(routine, log, ""), search, ["roomNumber", "teacherName", "className", "sectionName", "subjectName", "scheduledIn", "scheduledOut", "actualIn", "actualOut"]);
    });
  }, [routines, logByRoutine, search]);

  const summary = useMemo(() => ({
    "No time": routines.filter((routine) => {
      const log = logByRoutine[routine.id] || logByRoutine[routine.routineId];
      return !log?.actualIn && log?.classStatus !== "suspended";
    }).length,
    "Only IN": logs.filter((log) => log.actualIn && !log.actualOut && log.classStatus !== "suspended").length,
    "Completed": logs.filter((log) => log.actualIn && log.actualOut).length,
    "Suspended": logs.filter((log) => log.classStatus === "suspended").length
  }), [routines, logs, logByRoutine]);

  function handleDateChange(nextDate) {
    const nextDay = dayFromDate(nextDate);
    setDate(nextDate);
    setDay(nextDay);
    load(nextDate, nextDay);
  }

  function handleDayChange(nextDay) {
    setDay(nextDay);
    load(date, nextDay);
  }

  function openModal(routine, log) {
    const baseLog = log || makeLogFromRoutine(routine, date, day);
    setModal({
      log: baseLog,
      actualIn: baseLog.actualIn || getNowTimeValue(),
      actualOut: baseLog.actualOut || "",
      addOut: Boolean(baseLog.actualOut),
      classStatus: baseLog.classStatus || "normal",
      suspensionReason: baseLog.suspensionReason || ""
    });
  }

  async function handleSave(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await adminUpdateTimeLog(
        modal.log,
        {
          actualIn: modal.classStatus === "suspended" ? "" : modal.actualIn,
          actualOut: modal.classStatus === "suspended" ? "" : modal.addOut ? modal.actualOut : "",
          classStatus: modal.classStatus,
          suspensionReason: modal.classStatus === "suspended" ? modal.suspensionReason : ""
        },
        settings,
        user
      );
      showToast("Routine board updated");
      setModal(null);
      await load();
    } catch (error) {
      showToast(error.message || "Unable to save routine board", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="admin-page">
      <div className="page-title-row">
        <div>
          <p>Admin</p>
          <h2>Routine Board</h2>
        </div>
        <div className="toolbar-actions">
          <input type="date" value={date} onChange={(event) => handleDateChange(event.target.value)} />
          <select value={day} onChange={(event) => handleDayChange(event.target.value)}>
            {DAYS.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <button className="btn btn-primary" type="button" onClick={() => load()}><RefreshCw size={17} />Refresh</button>
        </div>
      </div>
      <SearchBar value={search} onChange={setSearch} placeholder="Search class, room, teacher, subject or time" />
      <RoutineBoardLegend />
      <div className="stats-grid routine-board-stats">
        {Object.entries(summary).map(([label, value], index) => (
          <StatCard key={label} label={label} value={value} icon={index === 0 ? "🔴" : index === 1 ? "🟡" : index === 2 ? "🟢" : "🔵"} tone={index === 0 ? "red" : index === 1 ? "orange" : index === 2 ? "green" : "blue"} />
        ))}
      </div>
      {loading ? (
        <SkeletonList />
      ) : visibleRoutines.length ? (
        <RoutineBoard routines={visibleRoutines} logs={logs} onCellClick={openModal} />
      ) : (
        <EmptyState icon="📋" title="No board data" text="Try another date, day or search." />
      )}

      <Modal open={Boolean(modal)} title="Edit Routine Time" description="Admin can correct IN/OUT or mark this cell as suspended." onClose={() => setModal(null)}>
        <form className="form-grid" onSubmit={handleSave}>
          <div className="selected-routine">
            <strong>{modal?.log?.subjectName} · Room {modal?.log?.roomNumber}</strong>
            <span>{modal?.log?.className} - {modal?.log?.sectionName} · {timeRangeLabel(modal?.log?.scheduledIn, modal?.log?.scheduledOut)}</span>
          </div>
          <Field label="Class status">
            <select value={modal?.classStatus || "normal"} onChange={(event) => setModal((value) => ({ ...value, classStatus: event.target.value }))}>
              <option value="normal">Normal</option>
              <option value="suspended">Suspended</option>
            </select>
          </Field>
          {modal?.classStatus === "suspended" ? (
            <Field label="Suspension reason">
              <input value={modal?.suspensionReason || ""} onChange={(event) => setModal((value) => ({ ...value, suspensionReason: event.target.value }))} placeholder="Reason" required />
            </Field>
          ) : (
            <>
              <Field label="IN Time">
                <input type="time" value={modal?.actualIn || ""} onChange={(event) => setModal((value) => ({ ...value, actualIn: event.target.value }))} required />
              </Field>
              <label className="optional-out-toggle">
                <input type="checkbox" checked={Boolean(modal?.addOut)} onChange={(event) => setModal((value) => ({ ...value, addOut: event.target.checked, actualOut: value.actualOut || getNowTimeValue() }))} />
                <span>Add OUT time</span>
              </label>
              {modal?.addOut ? (
                <Field label="OUT Time">
                  <input type="time" value={modal?.actualOut || ""} onChange={(event) => setModal((value) => ({ ...value, actualOut: event.target.value }))} required />
                </Field>
              ) : null}
            </>
          )}
          <div className="form-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setModal(null)}><X size={17} />Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? <span className="spinner" /> : <Save size={17} />}Save</button>
          </div>
        </form>
      </Modal>
    </section>
  );
}
